import React from 'react'

export default function Step10SaveSchedule({
  reportName,
  handleSave,
  isSaving,
  saveError,
  reportId = null,
  onTriggerSchedule,
  onTriggerExport,
}) {
  const [exportingFmt, setExportingFmt] = React.useState(null)

  const handleExport = async (fmt) => {
    if (exportingFmt) return
    setExportingFmt(fmt)
    try {
      await onTriggerExport?.(fmt)
    } finally {
      setExportingFmt(null)
    }
  }

  return (
    <div className="space-y-3.5 max-w-2xl mx-auto py-1">
      <div className="text-center space-y-1">
        <div className="w-10 h-10 bg-primary-fixed flex items-center justify-center rounded-[8px] mx-auto text-primary">
          <span className="material-symbols-outlined text-xl">task_alt</span>
        </div>
        <h3 className="text-base font-bold text-on-surface">Ready to Finalize Report</h3>
        <p className="text-[11px] text-on-surface-variant max-w-md mx-auto">
          Your custom report configuration is ready. Save it to your Reports Hub, schedule automated email dispatches, or export immediately.
        </p>
      </div>

      {saveError && (
        <div className="p-3 bg-error-container text-on-error-container rounded-[8px] text-xs font-semibold">
          {saveError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        {/* Save Report Card */}
        <div className="p-4 bg-surface-container-low rounded-[10px] border border-surface-container flex flex-col justify-between space-y-3">
          <div>
            <span className="material-symbols-outlined text-primary text-xl mb-1">save</span>
            <h4 className="text-xs font-bold text-on-surface">Save Report</h4>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              Store this report in the Global Reports Hub under <strong>My Reports</strong>.
            </p>
          </div>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave(false)}
            className="h-10 w-full bg-primary text-on-primary rounded-[8px] font-bold text-xs shadow-xs hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <span className="material-symbols-outlined text-sm">check</span>
            <span>{isSaving ? 'Saving...' : reportId ? 'Update Report' : 'Save & Publish'}</span>
          </button>
        </div>

        {/* Save As New Report Card */}
        <div className="p-4 bg-surface-container-low rounded-[10px] border border-surface-container flex flex-col justify-between space-y-3">
          <div>
            <span className="material-symbols-outlined text-secondary text-xl mb-1">content_copy</span>
            <h4 className="text-xs font-bold text-on-surface">Save As New Report</h4>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              Create an independent clone without modifying the original template.
            </p>
          </div>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave(true)}
            className="h-10 w-full bg-surface-container-high text-on-surface rounded-[8px] font-bold text-xs hover:bg-surface-container-highest transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <span className="material-symbols-outlined text-sm">content_copy</span>
            <span>Save as Clone</span>
          </button>
        </div>
      </div>

      {/* Instant Action Options */}
      <div className="p-3.5 bg-surface-container-low rounded-[10px] border border-surface-container space-y-2.5">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
          Instant Operations
        </h4>
        <div className="flex flex-wrap gap-2.5">
          <button
            type="button"
            disabled={Boolean(exportingFmt)}
            onClick={() => handleExport('xlsx')}
            className="h-9 flex items-center gap-1.5 px-3.5 bg-surface rounded-[8px] border border-surface-container-high text-xs font-semibold text-on-surface hover:bg-surface-container transition-colors disabled:opacity-50"
          >
            {exportingFmt === 'xlsx' ? (
              <span className="material-symbols-outlined animate-spin text-green-600 text-base">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-green-600 text-base">table_view</span>
            )}
            <span>{exportingFmt === 'xlsx' ? 'Exporting...' : 'Export Excel (.xlsx)'}</span>
          </button>
          <button
            type="button"
            disabled={Boolean(exportingFmt)}
            onClick={() => handleExport('csv')}
            className="h-9 flex items-center gap-1.5 px-3.5 bg-surface rounded-[8px] border border-surface-container-high text-xs font-semibold text-on-surface hover:bg-surface-container transition-colors disabled:opacity-50"
          >
            {exportingFmt === 'csv' ? (
              <span className="material-symbols-outlined animate-spin text-blue-600 text-base">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-blue-600 text-base">description</span>
            )}
            <span>{exportingFmt === 'csv' ? 'Exporting...' : 'Export CSV'}</span>
          </button>
          <button
            type="button"
            disabled={Boolean(exportingFmt)}
            onClick={() => handleExport('pdf')}
            className="h-9 flex items-center gap-1.5 px-3.5 bg-surface rounded-[8px] border border-surface-container-high text-xs font-semibold text-on-surface hover:bg-surface-container transition-colors disabled:opacity-50"
          >
            {exportingFmt === 'pdf' ? (
              <span className="material-symbols-outlined animate-spin text-red-600 text-base">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-red-600 text-base">picture_as_pdf</span>
            )}
            <span>{exportingFmt === 'pdf' ? 'Exporting...' : 'Export PDF'}</span>
          </button>
          <button
            type="button"
            onClick={() => onTriggerSchedule?.()}
            className="h-9 flex items-center gap-1.5 px-3.5 bg-surface rounded-[8px] border border-surface-container-high text-xs font-semibold text-on-surface hover:bg-surface-container transition-colors"
          >
            <span className="material-symbols-outlined text-purple-600 text-base">calendar_today</span>
            <span>Schedule Dispatch</span>
          </button>
        </div>
      </div>

    </div>
  )
}
